import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';

/**
 * Hook to check if App Inventory feature is allowed for current user.
 *
 * Uses value from TargetingContext which is computed in TargetingProviderWithAuth
 * based on:
 * - ENABLED_APP_INVENTORY feature flag
 * - ALLOWLIST_APP_INVENTORY allowlist (with wildcard domain support)
 *
 * @returns boolean - true if App Inventory is allowed for current user
 *
 * @example
 * ```typescript
 * const isAppInventoryAllowed = useAppInventoryAllowed();
 *
 * if (isAppInventoryAllowed) {
 *   // Render App Inventory UI
 * }
 * ```
 */
export const useAppInventoryAllowed = (): boolean => {
  const { isAppInventoryAllowed } = useTargetingContext();
  return isAppInventoryAllowed ?? false;
};

/**
 * Hook to check if the "App Bundle ID (From the Inventory List)" option is allowed within
 * App Inventory for the current app.
 *
 * Uses value from TargetingContext, configured per-app via `isAppInventoryInventoryListAllowed`
 * in the app's `targetingConfig`. Defaults to `true` (shown) when not explicitly configured.
 *
 * @returns boolean - true if the Inventory List option should be shown in App Inventory
 *
 * @example
 * ```typescript
 * const isInventoryListAllowed = useAppInventoryInventoryListAllowed();
 *
 * if (isInventoryListAllowed) {
 *   // Render Inventory List option
 * }
 * ```
 */
export const useAppInventoryInventoryListAllowed = (): boolean => {
  const { isAppInventoryInventoryListAllowed } = useTargetingContext();
  return isAppInventoryInventoryListAllowed ?? true;
};
